import {
  InitialConfigType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer';
import { SettingsContext } from '@/components/LexicalEditor/context/SettingsContext';
import { SharedAutocompleteContext } from '@/components/LexicalEditor/context/SharedAutocompleteContext';
import { SharedHistoryContext } from '@/components/LexicalEditor/context/SharedHistoryContext';
import PlaygroundNodes from '@/components/LexicalEditor/nodes/PlaygroundNodes';
import { TableContext } from '@/components/LexicalEditor/plugins/TablePlugin';
import PlaygroundEditorTheme from '@/components/LexicalEditor/themes/PlaygroundEditorTheme';
import LoadInitialContent from '@/components/LexicalEditor/LoadInitialContent';
import { TextNode } from 'lexical';
import { TableCellNode } from '@lexical/table';
import {
  $createExtendedTextNode,
  ExtendedTextNode,
} from '@/components/LexicalEditor/nodes/ExtendedTextNode';
import { ExtendedTableCellNode } from '@/components/LexicalEditor/nodes/ExtendedTableCellNode';
import { CSSProperties, PropsWithChildren } from 'react';
import UploadImageNode from '@/components/LexicalEditor/nodes/UploadImageNode';
import { AiDraftSectionNode } from '@/components/LexicalEditor/nodes/AiDraftSectionNode';
import { ToolbarContext } from '@/components/LexicalEditor/context/ToolbarContext';

export type LexicalContextProps = {
  value?: string;
  autoFocus?: boolean;
  config?: InitialConfigType;
  defaultFontSize?: number; // 화면별 본문 기본 폰트(px). 미지정 시 18.
};

export const defaultConfig = {
  editorState: undefined,
  namespace: 'Playground',
  nodes: [
    ExtendedTextNode,
    ExtendedTableCellNode,
    UploadImageNode,
    AiDraftSectionNode,
    ...PlaygroundNodes,
    // replace 는 반드시 PlaygroundNodes 뒤에 — 동일 노드 타입이 중복 등록되면 마지막 것만 적용됨
    {
      replace: TextNode,
      // getTextContent() 만 복사하면 교체 시 style/format 이 유실되니 함께 복사.
      with: (node: TextNode) => {
        const replaced = $createExtendedTextNode(node.getTextContent());
        replaced.setFormat(node.getFormat());
        replaced.setStyle(node.getStyle());
        replaced.setMode(node.getMode());
        replaced.setDetail(node.getDetail());
        return replaced;
      },
      withKlass: ExtendedTextNode,
    },
    {
      replace: TableCellNode,
      with: (node: TableCellNode) =>
        new ExtendedTableCellNode(
          node.__headerState,
          node.__colSpan,
          node.__width,
        ),
      withKlass: ExtendedTableCellNode,
    },
  ],
  onError: (error: Error) => {
    throw error;
  },
  theme: PlaygroundEditorTheme,
} as unknown as InitialConfigType;

function LexicalContext(props: PropsWithChildren<LexicalContextProps>) {
  const { value, children, config = defaultConfig, defaultFontSize } = props;

  return (
    <SettingsContext>
      <LexicalComposer initialConfig={config}>
        <LoadInitialContent initialContent={value ?? ''} />
        <SharedHistoryContext>
          <TableContext>
            <ToolbarContext defaultFontSize={defaultFontSize}>
              <SharedAutocompleteContext>
                <div
                  className="editor-shell"
                  style={
                    defaultFontSize
                      ? ({
                          '--lexical-default-font-size': `${defaultFontSize}px`,
                        } as CSSProperties)
                      : undefined
                  }
                >
                  {children}
                </div>
              </SharedAutocompleteContext>
            </ToolbarContext>
          </TableContext>
        </SharedHistoryContext>
      </LexicalComposer>
    </SettingsContext>
  );
}

export default LexicalContext;
